import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NSNavbar from '../../components/NSNavbar';
import NSFooter from '../../components/NSFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, PUBLISHER } from '../../data/legalContent';

function useNSTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'nutriscan');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

const TOC = [
  { id: 'who',      title: '1. Who we are' },
  { id: 'accept',   title: '2. Accepting these terms' },
  { id: 'audience', title: '3. Who may use NutriScan' },
  { id: 'service',  title: '4. What NutriScan provides' },
  { id: 'account',  title: '5. Your account' },
  { id: 'subs',     title: '6. Free allowance and subscriptions' },
  { id: 'changes',  title: '7. Changes to these terms' },
  { id: 'contact',  title: '8. Contact' },
];

export default function NSTerms() {
  useNSTheme();
  const supportHref = parentRouteHref('/contact');
  useDocumentMeta(routeMetaFor('/apps/nutriscan/terms'));

  return (
    <PageWrap testId="ns-terms-page">
      <NSNavbar />

      <header className="ns-legal-header" style={{ paddingTop: 'calc(var(--nav-h) + 40px)' }}>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ justifyContent: 'center', color: 'var(--ns-lime)' }}>NutriScan · Legal</div>
          <h1 className="reveal d1">Terms of Service</h1>
          <p className="reveal d2" style={{ marginTop: 8 }} data-testid="ns-terms-effective">
            Effective date: {EFFECTIVE_DATE} · {APP_LEGAL.nutriscan.version}
          </p>
        </div>
      </header>

      <div className="wrap">
        <div className="ns-summary-box reveal">
          <div className="title">The short version</div>
          <div className="ns-summary-grid">
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>NutriScan gives educational information about food labels — it is not medical, dietary, allergy, diagnostic, or emergency advice.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>NutriScan is intended for people aged 18 and over.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>A scan result is not promised to be accurate, complete, or safe for your situation.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>A subscription is optional and is managed in Google Play.</span></div>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop: 64, background: 'var(--ns-bg)' }}>
        <div className="wrap">
          <div className="legal-layout">
            <nav className="legal-toc">
              <h6>Contents</h6>
              <ul>{TOC.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ul>
            </nav>

            <div className="legal-body" style={{ background: 'var(--ns-surf)', borderColor: 'var(--ns-border)' }}>
              <span className="legal-meta-tag" data-testid="ns-terms-publisher">Published by {PUBLISHER} · Support: {SUPPORT_EMAIL}</span>

              <section id="who"><h2>1. Who we are</h2>
                <p>NutriScan is published by <strong>{PUBLISHER}</strong>. These terms apply to {APP_LEGAL.nutriscan.version} of the NutriScan mobile app.</p>
              </section>

              <section id="accept"><h2>2. Accepting these terms</h2>
                <p>By downloading, installing, or using NutriScan, you agree to these terms. If you do not agree, please do not use the app.</p>
              </section>

              <section id="audience"><h2>3. Who may use NutriScan</h2>
                <p>NutriScan is intended for people aged <strong>18 and over</strong> and is not directed to children.</p>
              </section>

              <section id="service"><h2>4. What NutriScan provides</h2>
                <p>NutriScan analyses a food label you submit and returns information in the app. That information is <strong>educational</strong>.</p>
                <div className="callout alert">
                  <i className="fas fa-triangle-exclamation"></i>
                  <span>NutriScan is <strong>not medical, dietary, allergy, diagnostic, or emergency advice</strong>. Do not rely on it to decide whether a food is safe for you.</span>
                </div>
                <p>We do not promise that a scan result is accurate, complete, or suitable for your circumstances. You remain responsible for your own decisions, including checking the product packaging itself and seeking qualified advice where that is appropriate. If you have a medical or allergy concern, speak to a qualified professional.</p>
              </section>

              <section id="account"><h2>5. Your account</h2>
                <p>NutriScan uses Google sign-in. Keep access to your Google account secure, because it controls access to your NutriScan account.</p>
                <p>You can ask us to delete your account at any time. The process, including the recovery period and what may be retained, is on the <Link to={brandPath('nutriscan', '/account-deletion')}>Account Deletion</Link> page.</p>
              </section>

              <section id="subs"><h2>6. Free allowance and subscriptions</h2>
                <p>NutriScan includes a free allowance of <strong>{APP_LEGAL.nutriscan.freeAllowance}</strong>.</p>
                <p>A subscription is optional. Purchases are processed by Google Play Billing, and you manage or cancel a subscription in Google Play. Deleting your NutriScan account does not by itself cancel a Google Play subscription.</p>
                <p>If the free allowance or subscription options change, this page and its effective date will be updated.</p>
              </section>

              <section id="changes"><h2>7. Changes to these terms</h2>
                <p>If these terms change, we update this page and the effective date shown at the top.</p>
              </section>

              <section id="contact"><h2>8. Contact</h2>
                <div className="callout info" style={{ background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--ns-green)' }}></i>
                  <span><strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a><br/>{PUBLISHER}</span>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.85rem' }}>
                  See also the <Link to={brandPath('nutriscan', '/privacy')}>NutriScan Privacy Policy</Link>, the{' '}
                  <Link to={brandPath('nutriscan', '/account-deletion')}>Account Deletion</Link> page, and the{' '}
                  <Link to={brandPath('nutriscan', '/faq')}>NutriScan FAQ</Link>. For anything else, use{' '}
                  {supportHref
                    ? <a href={supportHref}>TickleApps Support</a>
                    : <Link to="/contact">TickleApps Support</Link>}.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      <NSFooter />
    </PageWrap>
  );
}
